import { useState, useCallback, useRef } from 'react';
import { FFmpegService } from '../ffmpeg-service';
import {
    reencodeVideo,
    checkReplicateStatus,
    transcribeAudioConstance,
    callGemini, // Use standard Gemini
    callGeminiConstance,
    getR2UploadUrl,
    initiateMultipartUpload,
    getMultipartUploadUrls,
    completeMultipartUpload
} from '../services/firebase-api';
import { Subtitle } from '../types';

// Helper for VTT generation (moved from ConstancePage)
const formatTime = (seconds: number) => {
    const date = new Date(0);
    date.setSeconds(seconds);
    const line = date.toISOString().substr(11, 8);
    const ms = (seconds % 1).toFixed(3).substr(2);
    return `${line}.${ms}`;
};

const generateVtt = (segments: any[]) => {
    let vtt = "WEBVTT\n\n";
    segments.forEach((seg: any) => {
        const start = formatTime(seg.start);
        const end = formatTime(seg.end);
        vtt += `${start} --> ${end}\n${seg.text}\n\n`;
    });
    return URL.createObjectURL(new Blob([vtt], { type: 'text/vtt' }));
};

export interface MagicProcessState {
    isProcessing: boolean;
    isUploading: boolean;
    isOptimizing: boolean;
    uploadProgress: number;
    logs: string[];
    error: string | null;
    optimizedUrl: string | null; // The "light" video URL
    originalR2Url: string | null; // The master R2 URL
    subtitlesVttUrl: string | null;
    subtitleSegments: Subtitle[];
    analysisResult: string | null;
}

export const useMagicProcess = () => {
    const [state, setState] = useState<MagicProcessState>({
        isProcessing: false,
        isUploading: false,
        isOptimizing: false,
        uploadProgress: 0,
        logs: [],
        error: null,
        optimizedUrl: null,
        originalR2Url: null,
        subtitlesVttUrl: null,
        subtitleSegments: [],
        analysisResult: null
    });

    const addLog = useCallback((msg: string) => {
        setState(prev => ({ ...prev, logs: [...prev.logs, `[${new Date().toLocaleTimeString()}] ${msg}`] }));
    }, []);

    // Re-implement upload helper to avoid massive prop drilling or complex imports if not exported
    // ideally performUpload should be in a service. 
    // For now I'll duplicate the logic or we should confirm if we can export it from ConstancePage? 
    // Better: I will use the one I found in ConstancePage but I need to make sure I have all imports.
    // Actually, App.tsx and ConstancePage both have upload logic. 
    // I will implementation a local version here for now to be self-contained, 
    // relying on the service functions.

    const performUploadLocal = async (fileToUpload: File | Blob, type: 'audio' | 'video'): Promise<string> => {
        const isFile = fileToUpload instanceof File;
        const fileName = isFile ? (fileToUpload as File).name : `audio_extract_${Date.now()}.mp3`;
        const fileSize = fileToUpload.size;
        const ext = isFile ? fileName.split('.').pop() || 'mp4' : 'mp3';
        const contentType = type === 'audio' ? 'audio/mpeg' : (isFile ? (fileToUpload as File).type : 'video/mp4') || 'video/mp4';

        let CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
        let CONCURRENT_UPLOADS = 3;

        if (fileSize > 1024 * 1024 * 1024) {
            CHUNK_SIZE = 50 * 1024 * 1024; // 50MB
            CONCURRENT_UPLOADS = 5;
            addLog(`🚀 Mode Ultra-Fast activé (Chunks: 50MB, Threads: 5)`);
        }

        if (fileSize <= CHUNK_SIZE) {
            // Simple Upload
            const filename = `${type}_${Date.now()}.${ext}`;
            const result = await getR2UploadUrl({ fileName: filename, contentType });
            const { uploadUrl, publicUrl } = (result.data as any);

            await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', uploadUrl, true);
                xhr.setRequestHeader('Content-Type', contentType);
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) {
                        setState(prev => ({ ...prev, uploadProgress: Math.round((e.loaded / e.total) * 100) }));
                    }
                };
                xhr.onload = () => xhr.status === 200 ? resolve() : reject(new Error(`Status ${xhr.status}`));
                xhr.onerror = () => reject(new Error('Network Error'));
                xhr.send(fileToUpload);
            });
            return publicUrl;
        } else {
            // Multipart
            const totalParts = Math.ceil(fileSize / CHUNK_SIZE);
            addLog(`📦 Mode Multipart: ${totalParts} parties`);

            const initRes = await initiateMultipartUpload({ fileName: fileName, contentType });
            const { uploadId, key } = (initRes.data as any);

            const uploadedParts: { ETag: string, PartNumber: number }[] = [];

            for (let i = 0; i < totalParts; i += CONCURRENT_UPLOADS) {
                const batchIndices = [];
                for (let j = 0; j < CONCURRENT_UPLOADS && (i + j) < totalParts; j++) {
                    batchIndices.push(i + j);
                }
                const partNumbers = batchIndices.map(idx => idx + 1);
                const urlsRes = await getMultipartUploadUrls({ key, uploadId, partNumbers });
                const { urls } = (urlsRes.data as any);

                await Promise.all(batchIndices.map(async (partIndex) => {
                    const partNumber = partIndex + 1;
                    const start = partIndex * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, fileSize);
                    const chunk = fileToUpload.slice(start, end);

                    const signedUrlObj = urls.find((u: any) => u.partNumber === partNumber);
                    if (!signedUrlObj) throw new Error(`Missing URL for part ${partNumber}`);

                    const response = await fetch(signedUrlObj.url, { method: 'PUT', body: chunk });
                    if (!response.ok) throw new Error(`Failed to upload part ${partNumber}`);

                    const etag = response.headers.get('ETag');
                    if (!etag) throw new Error(`No ETag for part ${partNumber}`);

                    uploadedParts.push({ PartNumber: partNumber, ETag: etag.replace(/"/g, '') });

                    const percent = Math.round(((i + 1) / totalParts) * 100);
                    setState(prev => ({ ...prev, uploadProgress: percent }));
                }));
            }

            const completeRes = await completeMultipartUpload({ key, uploadId, parts: uploadedParts });
            return (completeRes.data as any).publicUrl;
        }
    };

    const startMagicProcess = useCallback(async (file: File) => {
        if (!file) return;

        // Reset state
        setState({
            isProcessing: true,
            isUploading: true,
            isOptimizing: true,
            uploadProgress: 0,
            logs: [],
            error: null,
            optimizedUrl: null,
            originalR2Url: null,
            subtitlesVttUrl: null,
            subtitleSegments: [],
            analysisResult: null
        });

        addLog(`🚀 Démarrage Magic Process pour: ${file.name}`);

        try {
            // Check FFmpeg
            try {
                await FFmpegService.getInstance();
            } catch (e) {
                addLog(`⚠️ FFmpeg non chargé, extraction audio locale impossible. Abort.`);
                throw new Error("FFmpeg required");
            }

            // --- BRANCH A: VIDEO OPTIMIZATION ---
            const videoProcess = async () => {
                addLog('🎥 [Vidéo] Update vers R2...');
                const masterUrl = await performUploadLocal(file, 'video');
                setState(prev => ({ ...prev, originalR2Url: masterUrl }));

                addLog(`🎥 [Vidéo] Optimisation Replicate...`);

                const result = await reencodeVideo({ videoUrl: masterUrl });
                const data = (result.data as any);

                if (!data.success || !data.predictionId) throw new Error("Echec Lancement Replicate");

                const predId = data.predictionId;
                const POLLING_INTERVAL = 3000;

                while (true) {
                    await new Promise(r => setTimeout(r, POLLING_INTERVAL));
                    const statusRes = await checkReplicateStatus({ predictionId: predId });
                    const statusData = (statusRes.data as any);

                    if (statusData.status === 'succeeded') {
                        const finalUrls = statusData.urls;
                        setState(prev => ({ ...prev, optimizedUrl: finalUrls[0], isOptimizing: false }));
                        addLog(`✅ [Vidéo] Optimisation terminée.`);
                        break;
                    } else if (statusData.status === 'failed' || statusData.status === 'canceled') {
                        throw new Error(`Replicate Failed: ${statusData.error}`);
                    }
                }
            };

            // --- BRANCH B: AUDIO & INTELLIGENCE ---
            const audioProcess = async () => {
                addLog('🎧 [Audio] Extraction MP3...');
                const blob = await FFmpegService.extractAudioFromLargeFile(file, () => { });
                const mp3File = new File([blob], "extract.mp3", { type: 'audio/mpeg' });

                addLog('🎧 [Audio] Upload MP3...');
                const mp3Url = await performUploadLocal(mp3File, 'audio');

                addLog('🧠 [Intelligence] Transcription Whisper (Force EN)...');
                const transcriptionJobId = `transcription_${Date.now()}_${Math.random().toString(36).substring(7)}`;

                // Polling function for Firestore
                const pollFirestoreForResult = async (): Promise<any> => {
                    const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
                    const db = getFirestore();
                    const jobRef = doc(db, 'transcription-jobs', transcriptionJobId);

                    return new Promise((resolve, reject) => {
                        const unsubscribe = onSnapshot(jobRef, (snapshot) => {
                            if (!snapshot.exists()) return;
                            const data = snapshot.data();
                            if (data?.status === 'complete') {
                                unsubscribe();
                                resolve({ data: { subtitles: data.subtitles, language: data.language } });
                            } else if (data?.status === 'error') {
                                unsubscribe();
                                reject(new Error(data.error || 'Transcription failed'));
                            }
                        });
                    });
                };

                const httpPromise = transcribeAudioConstance({
                    audioUrl: mp3Url,
                    language: 'en',
                    response_format: 'verbose_json',
                    jobId: transcriptionJobId
                }).catch((err: any) => {
                    console.log('HTTP dropped, waiting for Firestore:', err);
                    return null;
                });

                const transRes = await Promise.race([
                    httpPromise.then(r => r ? r : pollFirestoreForResult()),
                    pollFirestoreForResult()
                ]);

                const data = transRes.data as any;
                let transcriptText = "";

                if (data.subtitles && Array.isArray(data.subtitles)) {
                    transcriptText = data.subtitles.map((s: any) => s.text).join(' ');
                    setState(prev => ({
                        ...prev,
                        subtitleSegments: data.subtitles,
                        subtitlesVttUrl: generateVtt(data.subtitles)
                    }));
                    addLog(`📜 ${data.subtitles.length} sous-titres générés.`);
                }

                if (transcriptText) {
                    const geminiRes = await callGemini({
                        model: 'gemini-3.5-flash',
                        contents: [{
                            role: 'user',
                            parts: [{ text: `Analyze this transcript in the context of LVMH. identify key themes, luxury terminology, and sentiment. Transcript: ${transcriptText}` }]
                        }]
                    });

                    const responseData = geminiRes.data as any;
                    // Robust check for various Gemini response formats (native vs simplified)
                    let analysis = "Analogue non disponible.";

                    if (responseData.candidates && responseData.candidates[0]?.content?.parts?.[0]?.text) {
                        analysis = responseData.candidates[0].content.parts[0].text;
                    } else if (responseData.text) {
                        // Sometimes simplified wrappers return just text
                        analysis = responseData.text;
                    } else {
                        console.warn("[MagicProcess] Unexpected Gemini response format:", responseData);
                        addLog(`⚠️ [Gemini] Format de réponse inattendu (voir console).`);
                    }

                    setState(prev => ({ ...prev, analysisResult: analysis }));
                    addLog(`🧐 [Analyse] Terminée.`);
                }
            };

            // Run safely in parallel
            const safeAudioProcess = async () => {
                try { await audioProcess(); } catch (e: any) { addLog(`⚠️ [Audio] Erreur non bloquante: ${e.message}`); }
            };

            await Promise.all([videoProcess(), safeAudioProcess()]);

            addLog('🎉 WORKFLOW MAGIQUE TERMINÉ !');
            setState(prev => ({ ...prev, isProcessing: false, isUploading: false }));

        } catch (e: any) {
            console.error(e);
            setState(prev => ({ ...prev, isProcessing: false, isUploading: false, error: e.message }));
            addLog(`❌ Erreur: ${e.message}`);
        }

    }, [addLog]);

    return {
        startMagicProcess,
        state,
        addLog
    };
};
